// src/components/ModalCreatePlantilla.jsx
import React, { useMemo, useState, useEffect } from "react";
import api from "../../utils/axiosConfig";
import UploadConLoading from "./modalUploadFiles";
import ModalBaseSm from "./modal-base-sm";

const LS_KEY = "plantillas_cache_v1";

const SEED_PLANTILLAS = [
    { id: "tpl_1", nombre: "Orden de Trabajo Básica", descripcion: "Campos: Cliente, Placa, Servicio, Valor. Sin impuestos.", updatedAt: "2025-09-01T10:15:00.000Z" },
    { id: "tpl_2", nombre: "Orden con Geolocalización", descripcion: "Incluye lat/long y validación de geocerca para branches.", updatedAt: "2025-09-10T14:22:00.000Z" },
    { id: "tpl_3", nombre: "Conciliación Bancaria", descripcion: "Plantilla para subir extractos y conciliar pagos.", updatedAt: "2025-09-18T08:05:00.000Z" },
    { id: "tpl_4", nombre: "Reporte de Incidentes", descripcion: "Formulario para reportar y categorizar incidentes.", updatedAt: "2025-09-20T16:30:00.000Z" },
    { id: "tpl_5", nombre: "Encuesta de Satisfacción", descripcion: "Plantilla para encuestas post-servicio.", updatedAt: "2025-09-25T11:45:00.000Z" },
    { id: "tpl_6", nombre: "Registro de Mantenimiento", descripcion: "Formulario para registrar mantenimientos preventivos.", updatedAt: "2025-09-28T09:20:00.000Z" },
    { id: "tpl_7", nombre: "Control de Inventario", descripcion: "Plantilla para auditorías de inventario.", updatedAt: "2025-10-01T13:10:00.000Z" },
];

const safeId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tpl_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

export default function ModalCreatePlantilla({ onClose }) {
    const [templates, setTemplates] = useState([]);
    const [q, setQ] = useState("");
    const [mode, setMode] = useState(null); // 'create' | 'edit'
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ nombre: "", descripcion: "" });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshingId, setRefreshingId] = useState(null);

    const [modalUpload, setModalUpload] = useState(false);
    const [idEdit, setIdEdit] = useState(-1);

    const getPlantillas = async () => {
        setLoading(true);
        try {
            const res = await api.get("/operaciones/getplantillas");
            const payload = Array.isArray(res?.data?.data)
                ? res.data.data
                : (Array.isArray(res?.data) ? res.data : []);
            const finalData =
                Array.isArray(payload) && payload.length
                    ? payload
                    : (JSON.parse(localStorage.getItem(LS_KEY) || "[]") || []);
            const value = finalData.length ? finalData : SEED_PLANTILLAS;
            setTemplates(value);
            try { localStorage.setItem(LS_KEY, JSON.stringify(value)); } catch { }
        } catch {
            try {
                const cache = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
                setTemplates(cache.length ? cache : SEED_PLANTILLAS);
            } catch {
                setTemplates(SEED_PLANTILLAS);
            }
        } finally {
            setLoading(false);
        }
    };

    const onCloseModalUploadLoad = () => setModalUpload(false);

    const refreshOne = async (id) => {
        setRefreshingId(id);
        try { await getPlantillas(); }
        finally { setRefreshingId(null); }
    };

    useEffect(() => { getPlantillas(); }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return templates;
        return templates.filter(
            (t) =>
                (t?.nombre || "").toLowerCase().includes(needle) ||
                (t?.descripcion || "").toLowerCase().includes(needle)
        );
    }, [templates, q]);

    const openCreate = () => {
        setMode("create");
        setEditing(null);
        setForm({ nombre: "", descripcion: "" });
        setIdEdit(-1);
        setModalUpload(true);
    };

    const openEdit = (tpl) => {
        setIdEdit(tpl.id);
        setMode("edit");
        setEditing(tpl);
        setForm({ nombre: tpl?.nombre ?? "", descripcion: tpl?.descripcion ?? "" });
        setModalUpload(true);
    };

    const closeForm = () => {
        setMode(null);
        setEditing(null);
        setForm({ nombre: "", descripcion: "" });
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (!form.nombre.trim()) {
            alert("El nombre es requerido.");
            return;
        }
        setSaving(true);
        try {
            const now = new Date().toISOString();
            if (mode === "create") {
                const newTpl = {
                    id: safeId(),
                    nombre: form.nombre.trim(),
                    descripcion: form.descripcion?.trim() ?? "",
                    updatedAt: now,
                };
                setTemplates((prev) => [newTpl, ...prev]);
            } else if (mode === "edit" && editing?.id) {
                setTemplates((prev) =>
                    prev.map((t) =>
                        (t.id ?? t._id) === editing.id
                            ? { ...t, nombre: form.nombre.trim(), descripcion: form.descripcion?.trim() ?? "", updatedAt: now }
                            : t
                    )
                );
            }
            try { localStorage.setItem(LS_KEY, JSON.stringify((prev => prev)(templates))); } catch { }
            closeForm();
        } catch (err) {
            console.error(err);
            alert("No se pudo guardar.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {/* Contenedor principal: aseguramos que los hijos puedan scrollear */}
            <div className="flex flex-col min-h-0">
                {/* Body del modal con altura fija; la lista tomará el 80% */}
                <div className="p-4 md:p-6 flex flex-col gap-4 min-h-0 h-[70vh]">
                    {/* Buscador + conteo */}
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between shrink-0">
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por nombre o descripción…"
                            className="w-full rounded-xl border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500 md:max-w-sm"
                        />
                        <div className="text-sm text-gray-500">
                            {loading ? "Cargando…" : `${filtered.length} de ${templates.length} resultados`}
                        </div>
                    </div>

                    {/* Contenedor de CARDS: 80% de la altura del body del modal */}
                    <div style={{ scrollbarGutter: "stable", height: "60vh", overflowY: "auto" }}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {filtered.map((tpl) => {
                                const key = tpl.id ?? tpl._id ?? `${tpl.nombre}-${tpl.updatedAt}`;
                                const isRefreshing = refreshingId === (tpl.id ?? tpl._id);

                                return (
                                    <div
                                        key={key}
                                        className="flex flex-col justify-between rounded-2xl border border-gray-200 p-4 shadow-sm"
                                    >
                                        <div>
                                            <h3 className="line-clamp-1 text-base font-semibold">
                                                {tpl.nombre || "Sin nombre"}
                                            </h3>
                                            <p className="mt-1 line-clamp-3 text-sm text-gray-600">
                                                {tpl.descripcion || "—"}
                                            </p>
                                        </div>

                                        {/* Fila inferior: texto izquierda, lápiz derecha */}
                                        <div className="mt-4 flex w-full items-center justify-between gap-3 text-xs text-gray-500">
                                            <span className="flex-1 min-w-0 truncate">
                                                Actualizada: {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleString() : "—"}
                                            </span>
                                            <div className="flex flex-none items-center gap-2 whitespace-nowrap">
                                                <button
                                                    onClick={() => openEdit({ ...tpl, id: tpl.id ?? tpl._id ?? key })}
                                                    title="Editar plantilla"
                                                    aria-label="Editar plantilla"
                                                    className="inline-flex items-center rounded-lg p-2 hover:bg-indigo-50"
                                                >
                                                    <i className="bx bxs-pencil bx-tada" style={{ color: "#006699", fontSize: 18 }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Botón crear nueva (debajo; fuera del 80%) */}
                    <div className="flex justify-center shrink-0 mt-5">
                        <button onClick={openCreate} className="btn btn-primary btn-sm">
                            + Crear nueva plantilla
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal secundario para crear/editar (subida) */}
            <ModalBaseSm
                isOpen={modalUpload}
                title={idEdit === -1 ? "Crear plantilla" : "Editar plantilla"}
                onClose={onCloseModalUploadLoad}
                childComponent={
                    <UploadConLoading
                        onClose={onCloseModalUploadLoad}
                    />
                }
            />
        </>
    );
}
